import axios, {
  AxiosInstance,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosRequestConfig,
} from "axios";
import { loadingService, type LoaderType } from "@/shared/loading/loading.service";
import { toastService } from "@/shared/toast";
import { navigateToLoginReplace } from "@/app/navigation/appNavigation";
import { tokenService } from "@/services/auth/token.service";

// Extend axios config to support loader metadata
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  metadata?: {
    loaderType?: LoaderType;
    elementId?: string;
  };
}

// Store loader type per request
const loaderTypeMap = new WeakMap<InternalAxiosRequestConfig, LoaderType>();

export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get loader type from config metadata (defaults to "central")
    const extendedConfig = config as InternalAxiosRequestConfig & ExtendedAxiosRequestConfig;
    const loaderType: LoaderType = extendedConfig.metadata?.loaderType || "central";
    const elementId = extendedConfig.metadata?.elementId;

    // Store loader type for response interceptor
    loaderTypeMap.set(config, loaderType);

    // Show global loader for API calls
    // The loading service handles minimum display time internally
    loadingService.show(loaderType, elementId);

    // Add auth token if available
    const token = tokenService.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    // Hide loader on request error
    loadingService.hide();
    // Show toast after a small delay to ensure loader was visible
    setTimeout(() => {
      toastService.show("Network error. Please check your connection.", "error");
    }, 100);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Hide loader (service handles minimum display time)
    loadingService.hide();
    return response;
  },
  (error: AxiosError) => {
    // Get error message
    let errorMessage = "An error occurred";

    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data as { message?: string; error?: string } | undefined;

      errorMessage = data?.message || data?.error || `Request failed with status ${status}`;

      // Specific error messages
      if (status === 404) {
        errorMessage = data?.message || "Resource not found";
      } else if (status === 401) {
        errorMessage = "Unauthorized. Please login again.";
        tokenService.removeToken();
        navigateToLoginReplace();
        loadingService.hide();
        return Promise.reject(error);
      } else if (status === 403) {
        errorMessage = "You don't have permission to perform this action";
      } else if (status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
    } else if (error.request) {
      // Request was made but no response received
      errorMessage = "No response from server. Please check your connection.";
    } else {
      // Error setting up the request
      errorMessage = error.message || "An unexpected error occurred";
    }

    // Hide loader (service handles minimum display time)
    loadingService.hide();

    // Show toast notification after a small delay to ensure loader was visible first
    setTimeout(() => {
      toastService.show(errorMessage, "error");
    }, 100);

    return Promise.reject(error);
  }
);
